import { type FC, memo, useCallback, useEffect, useMemo, useState } from 'react'

import { Table } from 'common/components/Table/Table'
import { DEFAULT_TABLE_PARAMS_WITH_SEARCH } from 'common/constants/defaultTableParamsConstants'

import { SETTINGS_CONSTANTS } from 'features/Profile/features/Payment/constants/constants'

import { useNotification } from 'app/providers/notification/NotificationProvider'
import { TableSearchAndFilters } from 'common/components/TableSearchAndFilters/TableSearchAndFilters'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import type { TableParamsType } from 'common/interfaces/ITable'
import type { ObjectType } from 'common/interfaces/object'
import type { IPatient } from 'features/Patients/interfaces/IPatient'
import { useParams } from 'react-router'
import { TRANSACTIONS_MAPPING_TABLE_COLUMNS } from './columns'
import { ResolveTransactionModal } from './ResolveTransactionModal'
import type { ITransactionMappingItem } from './transactionsMapping.api'
import {
  useLazyFetchTransactionsMappingQuery,
  useResolveTransactionMutation,
} from './transactionsMapping.api'
import styles from './transactionsMapping.module.scss'

export const TransactionsMapping: FC = memo(() => {
  const [columns, setColumns] = useState<ObjectType[]>(TRANSACTIONS_MAPPING_TABLE_COLUMNS)
  const [tableParams, setTableParams] = useState<TableParamsType>({
    ...DEFAULT_TABLE_PARAMS_WITH_SEARCH,
  })
  const [resolveModalOpen, setResolveModalOpen] = useState(false)
  const [resolveRow, setResolveRow] = useState<any>(null)
  const [resolvingRowId, setResolvingRowId] = useState<number | null>(null)

  const { siteId } = useParams()

  const [fetchTransactionsMapping, { data: transactionsData, isFetching: isLoadingTransactions }] =
    useLazyFetchTransactionsMappingQuery()
  const [resolveTransaction, { isLoading: isResolvingTransaction }] =
    useResolveTransactionMutation()
  const { setNotification } = useNotification()

  const combinedData = useMemo(
    () => [
      ...(transactionsData?.transactions_with_appointment?.map((item) => ({
        ...item,
        type: 'transactions_with_appointment',
        isResolvingTransaction: isResolvingTransaction && resolvingRowId === item.id,
        onResolveWithAppointment: async (row: ITransactionMappingItem) => {
          if (!row.id || !row.patient_id || !row.appointment_id || !siteId) return
          setResolvingRowId(row.id)
          try {
            await resolveTransaction({
              id: row.id,
              patient_id: row.patient_id,
              encounter_id: row.appointment_id,
              siteId: siteId as string,
            })
          } finally {
            setResolvingRowId(null)
          }
        },
      })) || []),
      ...(transactionsData?.transactions_with_known_patient?.map((item) => ({
        ...item,
        type: 'transactions_with_known_patient',
        isResolvingTransaction: isResolvingTransaction && resolvingRowId === item.id,
        onResolveKnownPatient: async (row: ITransactionMappingItem) => {
          if (!row.id || !row.patient_id || !siteId) return
          setResolvingRowId(row.id)
          try {
            await resolveTransaction({
              id: row.id,
              patient_id: row.patient_id,
              encounter_id: 0,
              siteId: siteId as string,
            })
          } finally {
            setResolvingRowId(null)
          }
        },
      })) || []),
      ...(transactionsData?.transactions_with_new_patient?.map((item) => ({
        ...item,
        type: 'transactions_with_new_patient',
        isResolvingTransaction: isResolvingTransaction && resolvingRowId === item.id,
        onResolveKnownPatient: async (row: ITransactionMappingItem) => {
          setResolveRow(row)
          setResolveModalOpen(true)
          setResolvingRowId(row.id)
        },
      })) || []),
    ],
    [resolveTransaction, siteId, transactionsData, isResolvingTransaction, resolvingRowId],
  )

  const handleSelectPatient = useCallback(
    async (patient: IPatient, encounterEid: number) => {
      setResolveModalOpen(false)
      setResolveRow(null)
      if (!resolveRow?.id || !patient?.id || !encounterEid || !siteId) {
        return
      }

      try {
        await resolveTransaction({
          id: resolveRow.id,
          patient_id: patient.id,
          encounter_id: encounterEid,
          siteId: siteId as string,
        }).unwrap()
      } catch (e: any) {
        setNotification({
          title: 'Error',
          description: e?.data?.[0] || e?.message || 'Failed to resolve transaction',
          type: ALERT_CONSTANTS.ERROR,
        })
      }
    },
    [resolveRow, resolveTransaction, siteId],
  )

  useEffect(() => {
    if (tableParams.sorters) {
      setColumns((prev) =>
        prev.map((column) => ({
          ...column,
          sortOrder: tableParams.sorters?.[column.key]?.order,
        })),
      )
    }
  }, [tableParams.sorters])

  useEffect(() => {
    if (siteId) {
      fetchTransactionsMapping({
        siteId,
        tableParams: {
          ...tableParams,
          search: tableParams.debouncedValue?.length >= 2 ? tableParams.debouncedValue : '',
        },
      })
    }
  }, [siteId, tableParams.debouncedValue, tableParams.sorters])

  return (
    <div className={styles.parent}>
      <h1 className='page-title'>{SETTINGS_CONSTANTS.TRANSACTIONS_MAPPING}</h1>

      <TableSearchAndFilters
        setTableParams={setTableParams}
        setColumns={setColumns}
        tableParams={tableParams}
        inputHint='Search by transaction id'
      />
      <Table
        itemKey='id'
        haveActions={false}
        tableParams={tableParams}
        setTableParams={setTableParams}
        dataSource={combinedData}
        columns={columns.map((col) =>
          col.key === 'action' ? { ...col, isResolvingTransaction } : col,
        )}
        isLoading={isLoadingTransactions}
        pagination={false}
      />
      <ResolveTransactionModal
        isOpen={resolveModalOpen}
        onClose={() => setResolveModalOpen(false)}
        onSelectPatient={handleSelectPatient}
        row={resolveRow}
        type={resolveRow?.type}
      />
    </div>
  )
})
